import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getAccessToken, clearTokens } from "@/domains/auth/auth-storage";
import { refreshAccessToken, addToFailedQueue, isRefreshingToken } from "./token-refresh";
import { handleApiError } from "./error-handler";
import { navigateTo } from "@/shared/utils/navigate";

/**
 * Add request interceptor to Axios instance
 */
export const setupRequestInterceptor = (api: AxiosInstance) => {
  api.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

/**
 * Add response interceptor to Axios instance
 */
export const setupResponseInterceptor = (api: AxiosInstance) => {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status !== 401 || originalRequest._retry) {
        if (error.response?.status === 401 && originalRequest._retry) {
          console.error("Refresh token falhou ou é inválido.");
          clearTokens();
          navigateTo("/sign-out");
        }
        return Promise.reject(handleApiError(error));
      }

      if (isRefreshingToken()) {
        return new Promise((resolve, reject) => {
          addToFailedQueue(resolve, reject);
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }

        return api(originalRequest);
      } catch (refreshError: any) {
        return Promise.reject(handleApiError(refreshError));
      }
    }
  );
};
